/**
 * Gallery Card block — lightbox + Swiper.
 *
 * For each .block-gallery-card on the page:
 *   - Clicking .block-gallery-card__trigger opens its lightbox.
 *   - The lightbox is moved to <body> on init to avoid stacking-context issues.
 *
 * Two modes depending on block content:
 *   Video-only  — lightbox contains .block-gallery-card__video-container[data-video].
 *                 No Swiper. The oEmbed iframe is injected on open, removed on close.
 *   Pictures    — lightbox contains .js-gallery-card-swiper.
 *                 Swiper is initialised lazily on first open.
 */
export function initGalleryCard( Swiper, modules ) {
	const cards = document.querySelectorAll( '.block-gallery-card' );
	if ( ! cards.length ) return;

	cards.forEach( ( card ) => {
		const trigger        = card.querySelector( '.block-gallery-card__trigger' );
		const lightbox       = card.querySelector( '.block-gallery-card__lightbox' );
		const closeBtn       = lightbox?.querySelector( '.block-gallery-card__close' );
		const backdrop       = lightbox?.querySelector( '.block-gallery-card__backdrop' );
		const videoContainer = lightbox?.querySelector( '.block-gallery-card__video-container' );
		const swiperEl       = lightbox?.querySelector( '.js-gallery-card-swiper' );
		const isVideoOnly    = !! videoContainer;

		if ( ! trigger || ! lightbox ) return;

		// ── Move lightbox to <body> so fixed positioning is never clipped ─────
		document.body.appendChild( lightbox );

		let swiperInstance = null;

		// ── Helpers ───────────────────────────────────────────────────────────

		function injectVideo() {
			if ( ! videoContainer || videoContainer.innerHTML.trim() ) return;
			videoContainer.innerHTML = videoContainer.dataset.video || '';

			// Auto-play YouTube iframes by appending autoplay=1 to the src.
			const iframe = videoContainer.querySelector( 'iframe' );
			if ( iframe ) {
				const src = iframe.src || '';
				if ( /youtube\.com|youtu\.be/.test( src ) ) {
					const url = new URL( src );
					url.searchParams.set( 'autoplay', '1' );
					iframe.src = url.toString();
				}
			}
		}

		function clearVideo() {
			if ( videoContainer ) videoContainer.innerHTML = '';
		}

		// ── Open / close ──────────────────────────────────────────────────────

		function openLightbox() {
			lightbox.classList.add( 'is-open' );
			lightbox.setAttribute( 'aria-hidden', 'false' );
			document.body.classList.add( 'nav-open' );

			if ( isVideoOnly ) {
				injectVideo();
			} else if ( swiperEl ) {
				if ( ! swiperInstance ) {
					swiperInstance = new Swiper( swiperEl, {
						modules: Object.values( modules ),
						slidesPerView: 1,
						spaceBetween: 0,
						keyboard: { enabled: true },
						pagination: {
							el: swiperEl.querySelector( '.swiper-pagination' ),
							clickable: true,
						},
						navigation: {
							nextEl: swiperEl.querySelector( '.swiper-button-next' ),
							prevEl: swiperEl.querySelector( '.swiper-button-prev' ),
						},
					} );
				}
			}

			closeBtn?.focus();
		}

		function closeLightbox() {
			if ( isVideoOnly ) {
				clearVideo();
			}
			lightbox.classList.remove( 'is-open' );
			lightbox.setAttribute( 'aria-hidden', 'true' );
			document.body.classList.remove( 'nav-open' );
			trigger.focus();
		}

		// ── Event listeners ───────────────────────────────────────────────────

		trigger.addEventListener( 'click', openLightbox );
		closeBtn?.addEventListener( 'click', closeLightbox );
		backdrop?.addEventListener( 'click', closeLightbox );

		document.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Escape' && lightbox.classList.contains( 'is-open' ) ) {
				closeLightbox();
			}
		} );
	} );
}
